import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export type DashboardCard = {
  id: string;
  title: string;
  description: string;
  icon: any;
  iconName: string;
  href: string;
  roles: readonly string[];
  tier: string;
};

export type DashboardSection = {
  id: string;
  title: string;
  subtitle: string;
  icon: any;
  iconName: string;
  tier: string;
  cards: DashboardCard[];
};

export const useDashboardPreferences = () => {
  const { user, userRoles } = useAuth();
  const [layoutDesktop, setLayoutDesktop] = useState<any[]>([]);
  const [layoutMobile, setLayoutMobile] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Get user's primary role for loading preferences
  const getUserRole = () => {
    if (!userRoles || userRoles.length === 0) return null;
    // Priority order: dev, admin, staff, instructor
    if (userRoles.includes('dev')) return 'dev';
    if (userRoles.includes('admin')) return 'admin';
    if (userRoles.includes('staff')) return 'staff';
    if (userRoles.includes('instructor')) return 'instructor';
    return null;
  };

  // Load preferences from database
  useEffect(() => {
    const loadPreferences = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      const role = getUserRole();
      if (!role) {
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('dashboard_preferences')
          .select('layout_desktop, layout_mobile')
          .eq('role', role)
          .maybeSingle();

        if (error) throw error;

        if (data) {
          setLayoutDesktop(Array.isArray(data.layout_desktop) ? data.layout_desktop : []);
          setLayoutMobile(Array.isArray(data.layout_mobile) ? data.layout_mobile : []);
        }
      } catch (error) {
        console.error('Error loading dashboard preferences:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadPreferences();

    // Subscribe to real-time updates for dashboard preferences
    const role = getUserRole();
    if (!role) return;

    const channel = supabase
      .channel(`dashboard-preferences-changes-${role}-${user.id}-${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'dashboard_preferences',
          filter: `role=eq.${role}`
        },
        (payload) => {
          console.log('Dashboard preferences updated:', payload);
          if (payload.new && typeof payload.new === 'object' && 'layout_desktop' in payload.new) {
            const newData = payload.new as any;
            setLayoutDesktop(Array.isArray(newData.layout_desktop) ? newData.layout_desktop : []);
            setLayoutMobile(Array.isArray(newData.layout_mobile) ? newData.layout_mobile : []);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, userRoles]);

  // Save preferences to database (only for admin/dev, affects staff and instructor)
  const savePreferences = async (desktop: any[], mobile: any[]) => {
    if (!user || !userRoles) return { success: false, error: 'No user' };

    const isAdminOrDev = userRoles.includes('admin') || userRoles.includes('dev');
    if (!isAdminOrDev) {
      return { success: false, error: 'Unauthorized' };
    }

    try {
    // Determine which roles to update based on user's role
    const rolesToUpdate = userRoles.includes('dev') 
      ? (['dev', 'admin', 'staff', 'instructor'] as const)
      : (['admin', 'staff', 'instructor'] as const);
      
      for (const role of rolesToUpdate) {
        const { error } = await supabase
          .from('dashboard_preferences')
          .upsert({
            role: role as any,
            layout_desktop: desktop,
            layout_mobile: mobile,
            updated_at: new Date().toISOString(),
          }, {
            onConflict: 'role'
          });

        if (error) throw error;
      }

      setLayoutDesktop(desktop);
      setLayoutMobile(mobile);
      return { success: true };
    } catch (error) {
      console.error('Error saving dashboard preferences:', error);
      return { success: false, error };
    }
  };

  return {
    layoutDesktop,
    layoutMobile,
    isLoading,
    savePreferences,
  };
};